// Visual effects and animations system for React with performance optimizations
export interface Particle {
  id: string;
  type: 'confetti' | 'text' | 'sparkle' | 'explosion';
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  life: number;
  decay: number;
  size: number;
  text?: string;
  rotation?: number;
  rotationSpeed?: number;
}

export interface GlowEffect {
  id: string;
  element: HTMLElement;
  color: string;
  intensity: number;
  pulsePhase: number;
  originalBoxShadow: string;
  originalFilter: string;
}

export interface EffectConfig {
  type: 'confetti' | 'coin' | 'explosion' | 'glow' | 'screenShake';
  position: { x: number; y: number };
  duration?: number;
  color?: string;
  count?: number;
  value?: number;
  intensity?: number;
}

export class EffectsManager {
  private particles: Particle[] = [];
  private glowEffects = new Map<string, GlowEffect>();
  private screenShakeActive = false;
  private animationFrame: number | null = null;
  private container: HTMLElement | null = null;
  private particleContainer: HTMLElement | null = null;
  private toastHost: HTMLElement | null = null;
  private particlePool: HTMLElement[] = [];
  private activeParticles = new Map<string, HTMLElement>();
  private maxParticles = 100;
  private lastFrameTime = 0;
  private frameInterval = 1000 / 60;
  private isAnimating = false;

  constructor(container?: HTMLElement) {
    this.container = container || document.getElementById('game-container') || document.getElementById('game') || document.body;
    this.initializeParticleContainer();
    // Don't start animation loop in constructor - start on demand
  }

  private initializeParticleContainer(): void {
    // Create a dedicated container for particles
    this.particleContainer = document.createElement('div');
    this.particleContainer.className = 'particle-effects-container';
    this.particleContainer.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
      will-change: transform;
    `;
    document.body.appendChild(this.particleContainer);

    // Pre-create particle elements for pooling
    for (let i = 0; i < this.maxParticles; i++) {
      const element = document.createElement('div');
      element.className = 'particle';
      element.style.cssText = `
        position: absolute;
        pointer-events: none;
        will-change: transform, opacity;
        backface-visibility: hidden;
        transform-style: preserve-3d;
      `;
      this.particlePool.push(element);
    }
  }

  private getToastHost(): HTMLElement {
    if (this.toastHost && this.toastHost.isConnected) {
      return this.toastHost;
    }

    const existingHost = document.getElementById('game-effects-toast-host');
    if (existingHost) {
      this.toastHost = existingHost;
      return existingHost;
    }

    const host = document.createElement('div');
    host.id = 'game-effects-toast-host';
    host.style.cssText = `
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 10000;
    `;
    document.body.appendChild(host);
    this.toastHost = host;
    return host;
  }

  // Convert percentage coordinates to pixel coordinates
  private percentToPixel(value: number, isX: boolean): number {
    const viewport = this.container || document.body;
    const rect = viewport.getBoundingClientRect();
    const dimension = isX ? rect.width : rect.height;
    
    // If value is between 0-100, treat as percentage
    if (value >= 0 && value <= 100) {
      return (value / 100) * dimension + (isX ? rect.left : rect.top);
    }
    // Otherwise treat as pixel value
    return value;
  }

  private ensureAnimating(): void {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.lastFrameTime = 0;
    this.animationFrame = requestAnimationFrame((t) => this.tick(t));
  }

  private tick(currentTime: number): void {
    // Stop loop when nothing to process
    if (this.particles.length === 0 && this.glowEffects.size === 0 && !this.screenShakeActive) {
      this.isAnimating = false;
      this.animationFrame = null;
      return;
    }

    // Frame rate limiting
    const deltaTime = currentTime - this.lastFrameTime;
    if (deltaTime >= this.frameInterval) {
      this.update(deltaTime);
      this.lastFrameTime = currentTime - (deltaTime % this.frameInterval);
    }

    this.animationFrame = requestAnimationFrame((t) => this.tick(t));
  }

  private update(deltaTime: number) {
    this.updateParticles(deltaTime);
    this.updateGlowEffects();
    this.render();
  }

  // Particle effects
  createConfetti(x: number, y: number, count = 20): void {
    // Limit particle count
    count = Math.min(count, this.maxParticles - this.particles.length);
    if (count <= 0) return;
    this.ensureAnimating();

    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b'];
    const pixelX = this.percentToPixel(x, true);
    const pixelY = this.percentToPixel(y, false);

    for (let i = 0; i < count; i++) {
      this.particles.push({
        id: `confetti-${Date.now()}-${i}`,
        type: 'confetti',
        x: pixelX,
        y: pixelY,
        vx: (Math.random() - 0.5) * 10,
        vy: Math.random() * -8 - 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1.0,
        decay: 0.02,
        size: Math.random() * 10 + 6,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
      });
    }
  }

  createCoinEffect(x: number, y: number, value: number): void {
    this.ensureAnimating();
    const pixelX = this.percentToPixel(x, true);
    const pixelY = this.percentToPixel(y, false);
    
    // Floating "+X" text effect
    this.particles.push({
      id: `coin-text-${Date.now()}`,
      type: 'text',
      x: pixelX,
      y: pixelY,
      vx: 0,
      vy: -2,
      text: `+${value}`,
      color: '#f1c40f',
      life: 1.0,
      decay: 0.01,
      size: 16,
    });

    // Reduced sparkle particles for performance
    for (let i = 0; i < 4; i++) {
      this.particles.push({
        id: `coin-sparkle-${Date.now()}-${i}`,
        type: 'sparkle',
        x: pixelX + (Math.random() - 0.5) * 30,
        y: pixelY + (Math.random() - 0.5) * 30,
        vx: (Math.random() - 0.5) * 3,
        vy: (Math.random() - 0.5) * 3,
        color: '#f1c40f',
        life: 1.0,
        decay: 0.03,
        size: Math.random() * 4 + 2,
      });
    }
  }

  createExplosion(x: number, y: number, color = '#ff4757'): void {
    this.ensureAnimating();
    const pixelX = this.percentToPixel(x, true);
    const pixelY = this.percentToPixel(y, false);
    
    for (let i = 0; i < 10; i++) { // Reduced from 15
      const angle = (i / 10) * Math.PI * 2;
      const speed = Math.random() * 5 + 3;

      this.particles.push({
        id: `explosion-${Date.now()}-${i}`,
        type: 'explosion',
        x: pixelX,
        y: pixelY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        color,
        life: 1.0,
        decay: 0.04,
        size: Math.random() * 8 + 4,
      });
    }
  }

  createSparkle(x: number, y: number, count = 10, color = '#4ecdc4'): void {
    this.ensureAnimating();
    const pixelX = this.percentToPixel(x, true);
    const pixelY = this.percentToPixel(y, false);
    
    count = Math.min(count, 6); // Limit sparkles for performance
    
    for (let i = 0; i < count; i++) {
      const angle = (Math.random() * Math.PI * 2);
      const speed = Math.random() * 2 + 1;
      
      this.particles.push({
        id: `sparkle-${Date.now()}-${i}`,
        type: 'sparkle',
        x: pixelX + (Math.random() - 0.5) * 20,
        y: pixelY + (Math.random() - 0.5) * 20,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 1,
        color,
        life: 1.0,
        decay: 0.025,
        size: Math.random() * 6 + 2,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 15,
      });
    }
  }

  // Screen flash effect
  createScreenFlash(color = '#ffffff', opacity = 0.5): void {
    const flash = document.createElement('div');
    flash.className = 'screen-flash';
    flash.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${color};
      opacity: 0;
      pointer-events: none;
      z-index: 9998;
      will-change: opacity;
    `;
    document.body.appendChild(flash);
    
    // Use CSS animation for better performance
    requestAnimationFrame(() => {
      flash.style.transition = 'opacity 0.1s ease-out';
      flash.style.opacity = opacity.toString();
      
      setTimeout(() => {
        flash.style.opacity = '0';
        setTimeout(() => flash.remove(), 100);
      }, 100);
    });
  }

  // Level transition effect
  animateLevelTransition(callback?: () => void): void {
    const transition = document.createElement('div');
    transition.className = 'screen-transition wipe-circular';
    transition.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: radial-gradient(circle, transparent 0%, #000 100%);
      pointer-events: none;
      z-index: 9998;
      animation: wipeIn 0.6s ease-out forwards;
    `;
    document.body.appendChild(transition);
    
    setTimeout(() => {
      transition.remove();
      if (callback) callback();
    }, 600);
  }

  // Achievement popup
  showAchievement(text: string, icon = 'trophy'): void {
    const popup = document.createElement('div');
    popup.className = 'achievement-popup achievement-toast';
    popup.innerHTML = `
      <div class="achievement-icon">${icon}</div>
      <div class="achievement-text">${text}</div>
    `;
    popup.style.cssText = `
      position: fixed;
      top: var(--game-toast-top, calc(64px + env(safe-area-inset-top, 0px)));
      left: 50%;
      background: var(--pixel-bg-primary);
      border: 3px solid var(--pixel-border);
      padding: 20px;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      transform: translate(-50%, -22px) scale(0.9);
      opacity: 0;
      transition: transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.45s ease-out;
      z-index: 1;
      min-width: 240px;
      max-width: min(92vw, 520px);
      text-align: center;
    `;
    this.getToastHost().appendChild(popup);
    
    requestAnimationFrame(() => {
      popup.style.opacity = '1';
      popup.style.transform = 'translate(-50%, 0) scale(1)';
    });
    
    setTimeout(() => {
      popup.style.opacity = '0';
      popup.style.transform = 'translate(-50%, -20px) scale(0.94)';
      setTimeout(() => popup.remove(), 500);
    }, 2000);
  }

  // Glow effects
  addGlow(element: HTMLElement, color = '#4ecdc4', intensity = 1): string {
    const glowId = `glow-${Date.now()}-${Math.random()}`;

    const originalBoxShadow = element.style.boxShadow;
    const originalFilter = element.style.filter;

    element.style.boxShadow = `0 0 ${20 * intensity}px ${color}`;
    element.style.filter = `drop-shadow(0 0 ${10 * intensity}px ${color})`;

    this.glowEffects.set(glowId, {
      id: glowId,
      element,
      color,
      intensity,
      pulsePhase: 0,
      originalBoxShadow,
      originalFilter,
    });

    this.ensureAnimating();
    return glowId;
  }

  removeGlow(glowId: string): void {
    const glow = this.glowEffects.get(glowId);
    if (glow) {
      glow.element.style.boxShadow = glow.originalBoxShadow;
      glow.element.style.filter = glow.originalFilter;
      this.glowEffects.delete(glowId);
    }
  }

  // Screen shake
  screenShake(duration = 500, intensity = 5): void {
    if (this.screenShakeActive) return;

    this.screenShakeActive = true;
    this.ensureAnimating();
    const startTime = Date.now();
    const gameContainer = this.container || document.body;
    const originalTransform = gameContainer.style.transform;

    const shake = () => {
      const elapsed = Date.now() - startTime;
      const progress = elapsed / duration;

      if (progress >= 1) {
        this.screenShakeActive = false;
        gameContainer.style.transform = originalTransform;
        return;
      }

      const currentIntensity = intensity * (1 - progress);
      const x = (Math.random() - 0.5) * currentIntensity;
      const y = (Math.random() - 0.5) * currentIntensity;

      gameContainer.style.transform = `translate(${x}px, ${y}px)`;
      requestAnimationFrame(shake);
    };

    shake();
  }

  // Character animations
  animateCharacterMove(element: HTMLElement): void {
    element.style.transform = 'scale(1.1)';
    element.style.transition = 'transform 0.1s ease-out';

    setTimeout(() => {
      element.style.transform = 'scale(1)';
    }, 100);
  }

  animateCharacterImpact(element: HTMLElement): void {
    element.style.transform = 'scale(0.9) rotate(5deg)';
    element.style.transition = 'transform 0.1s ease-out';

    setTimeout(() => {
      element.style.transform = 'scale(1) rotate(0deg)';
    }, 150);
  }

  animateCharacterVictory(element: HTMLElement): void {
    // Add victory animation class for 3 jumps
    element.classList.add('animate-character-victory-jump');
    
    // Create jump animation with 3 bounces
    const jumpHeight = 30;
    const jumpDuration = 300;
    const totalJumps = 3;
    
    for (let i = 0; i < totalJumps; i++) {
      setTimeout(() => {
        // Jump up
        element.style.transform = `translateY(-${jumpHeight}px) scale(1.1)`;
        element.style.transition = `transform ${jumpDuration}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        
        // Land down
        setTimeout(() => {
          element.style.transform = 'translateY(0) scale(1)';
          element.style.transition = `transform ${jumpDuration}ms cubic-bezier(0.6, 0, 0.8, 1)`;
          
          // Add dust puff effect on landing
          this.createDustPuff(element);
        }, jumpDuration);
      }, i * (jumpDuration * 2) + 100);
    }
    
    // Clean up after animation
    setTimeout(() => {
      element.classList.remove('animate-character-victory-jump');
      element.style.transform = '';
      element.style.transition = '';
    }, totalJumps * jumpDuration * 2 + 500);
  }
  
  private createDustPuff(element: HTMLElement): void {
    const dust = document.createElement('div');
    dust.style.cssText = `
      position: absolute;
      bottom: -5px;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 10px;
      background: radial-gradient(ellipse, rgba(139, 90, 43, 0.6) 0%, transparent 70%);
      border-radius: 50%;
      animation: dust-puff 0.4s ease-out forwards;
      pointer-events: none;
    `;
    
    element.appendChild(dust);
    setTimeout(() => dust.remove(), 400);
  }

  // Trigger effect from config
  triggerEffect(config: EffectConfig): void {
    const { type, position, duration = 1000, color, count, value, intensity } = config;

    switch (type) {
      case 'confetti':
        this.createConfetti(position.x, position.y, count || 20);
        break;
      case 'coin':
        this.createCoinEffect(position.x, position.y, value || 10);
        break;
      case 'explosion':
        this.createExplosion(position.x, position.y, color || '#ff4757');
        break;
      case 'screenShake':
        this.screenShake(duration, intensity || 5);
        break;
    }
  }

  // Update methods with performance optimizations
  private updateParticles(deltaTime: number): void {
    const dt = deltaTime / 16.67; // Normalize to 60 FPS
    
    this.particles = this.particles.filter(particle => {
      particle.x += particle.vx * dt;
      particle.y += particle.vy * dt;
      particle.vy += 0.2 * dt; // Gravity
      particle.life -= particle.decay * dt;

      if (particle.rotation !== undefined && particle.rotationSpeed !== undefined) {
        particle.rotation += particle.rotationSpeed * dt;
      }

      return particle.life > 0;
    });
  }

  private updateGlowEffects(): void {
    this.glowEffects.forEach(glow => {
      glow.pulsePhase += 0.1;
      const pulse = Math.sin(glow.pulsePhase) * 0.3 + 0.7;
      const intensity = glow.intensity * pulse;

      glow.element.style.boxShadow = `0 0 ${20 * intensity}px ${glow.color}`;
      glow.element.style.filter = `drop-shadow(0 0 ${10 * intensity}px ${glow.color})`;
    });
  }

  private render(): void {
    // Use object pooling and CSS transforms for better performance
    const particlesToRender = this.particles.slice(0, this.maxParticles);
    
    // Remove particles that are no longer active
    this.activeParticles.forEach((element, id) => {
      if (!particlesToRender.find(p => p.id === id)) {
        element.style.display = 'none';
        this.particlePool.push(element);
        this.activeParticles.delete(id);
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      }
    });

    // Render active particles
    particlesToRender.forEach(particle => {
      let element = this.activeParticles.get(particle.id);
      
      if (!element && this.particlePool.length > 0) {
        element = this.particlePool.pop()!;
        element.className = `particle particle-${particle.type}`;
        element.style.display = 'block';
        this.activeParticles.set(particle.id, element);
        this.particleContainer?.appendChild(element);
      }

      if (element) {
        // Use transform for positioning (GPU accelerated)
        const transform = `translate3d(${particle.x}px, ${particle.y}px, 0) ${
          particle.rotation !== undefined ? `rotate(${particle.rotation}deg)` : ''
        }`;
        
        element.style.transform = transform;
        element.style.width = `${particle.size}px`;
        element.style.height = `${particle.size}px`;
        element.style.backgroundColor = particle.type === 'text' ? 'transparent' : particle.color;
        element.style.opacity = particle.life.toString();

        if (particle.type === 'text' && particle.text) {
          element.textContent = particle.text;
          element.style.fontSize = `${particle.size}px`;
          element.style.fontWeight = 'bold';
          element.style.color = particle.color;
          element.style.fontFamily = '"Press Start 2P", monospace';
        }
      }
    });
  }

  // Show specific effects (for backward compatibility)
  showConfetti(): void {
    const centerX = 50; // Use percentage
    const centerY = 50;
    this.createConfetti(centerX, centerY, 30);
  }

  showExplosion(x?: number, y?: number): void {
    const posX = x || 50; // Use percentage
    const posY = y || 50;
    this.createExplosion(posX, posY);
  }

  // Cleanup
  destroy(): void {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    
    // Clear all glow effects
    this.glowEffects.forEach(glow => {
      glow.element.style.boxShadow = glow.originalBoxShadow;
      glow.element.style.filter = glow.originalFilter;
    });
    this.glowEffects.clear();

    // Remove particle container
    if (this.particleContainer && this.particleContainer.parentNode) {
      this.particleContainer.parentNode.removeChild(this.particleContainer);
    }

    if (this.toastHost && this.toastHost.parentNode) {
      this.toastHost.parentNode.removeChild(this.toastHost);
      this.toastHost = null;
    }

    // Clear particles
    this.particles = [];
    this.activeParticles.clear();
    this.particlePool = [];

    // Reset screen shake
    if (this.screenShakeActive) {
      const gameContainer = this.container || document.body;
      gameContainer.style.transform = '';
      this.screenShakeActive = false;
    }
  }
}

// Add required CSS animation
const style = document.createElement('style');
style.textContent = `
  @keyframes wipeIn {
    from { transform: scale(0); }
    to { transform: scale(2); }
  }
`;
document.head.appendChild(style);

export default EffectsManager;
